import { useLazyQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'

import { SelectTags } from 'components/v2/SelectTags'
import { ISelectTagsProps } from 'components/v2/SelectTags/types'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_VEHICLES_SUGGESTIONS } from 'features/Vehicles/queries-legacy'
import { getVehicleLabelWithExternalId } from 'features/Vehicles/utils'
import { GTVehicleSuggestion, GTVehicleSuggestionsResponse } from 'gql/types.generated'
import { isEmpty } from 'lodash'
import { MAX_INT_FOR_PAGINATION_SIZE } from 'config/constants'

interface IMultiVehicleAutocompleteProps extends Omit<ISelectTagsProps, 'onChange' | 'items' | 'getTagLabel'> {
  onChange: (vehicles: GTVehicleSuggestion[]) => void
  partnerId?: string
  isShowingAllVehiclesOption?: boolean
}

const ALL_VEHICLES_OPTION_ID = 'all'
const ALL_VEHICLES_OPTION: GTVehicleSuggestion = {
  id: ALL_VEHICLES_OPTION_ID,
  externalID: '',
  name: 'All Vehicles',
  capacity: 0
}

export const MultiVehicleAutocomplete = ({
  onChange,
  label,
  placeholder,
  values,
  partnerId,
  isShowingAllVehiclesOption = false,
  ...props
}: IMultiVehicleAutocompleteProps): ReactElement => {
  const [isLoadingAllVehicles, setIsLoadingAllVehicles] = useState(false)
  const [pattern, setPattern] = useState('')
  const [getVehicles, { data, loading, error }] = useLazyQuery<{
    vehicleSuggestions: GTVehicleSuggestionsResponse
  }>(GET_VEHICLES_SUGGESTIONS)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getVehicles,
    pattern,
    extraInputs: {
      partnerID: partnerId || null
    }
  })
  const getAllVehicles = async (): Promise<GTVehicleSuggestion[]> => {
    try {
      setIsLoadingAllVehicles(true)
      const { data: vehiclesData } = await getVehicles({
        variables: {
          input: {
            partnerID: partnerId
          },
          pagination: {
            page: 1,
            size: MAX_INT_FOR_PAGINATION_SIZE
          }
        }
      })
      setIsLoadingAllVehicles(false)
      return vehiclesData?.vehicleSuggestions?.items || []
    } catch {
      setIsLoadingAllVehicles(false)
      return []
    }
  }

  const selectedVehicleIds = values.map(({ id }) => id)
  const filteredItems =
    data?.vehicleSuggestions?.items.filter((vehicle) => {
      return !selectedVehicleIds.includes(vehicle.id)
    }) || []

  const handleChange = async (vehicles: GTVehicleSuggestion[], pattern = ''): Promise<void> => {
    setPattern(pattern)

    const isSelectingAllVehicleOption = vehicles.some(({ id }) => id === ALL_VEHICLES_OPTION_ID)

    if (isSelectingAllVehicleOption) {
      const allVehicles = await getAllVehicles()
      onChange(allVehicles)
    } else {
      onChange(vehicles)
    }
  }

  const items = isEmpty(pattern) && isShowingAllVehiclesOption ? [ALL_VEHICLES_OPTION, ...filteredItems] : filteredItems

  return (
    <SelectTags
      placeholder={placeholder}
      values={values}
      label={label}
      onChange={handleChange}
      items={items}
      getTagLabel={(vehicle: GTVehicleSuggestion): string => getVehicleLabelWithExternalId(vehicle)}
      loading={loading || isDebouncing}
      error={error}
      shouldShowItems={shouldShowItems}
      disabled={props.disabled || isLoadingAllVehicles}
      {...props}
    />
  )
}
